import {
    faCheckCircle,
    faTimesCircle,
    IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { memo } from 'react';

export type BooleanStateIconProps = {
    /**
     * Icon to display for this state.
     */
    icon?: IconDefinition;
    /**
     * Color of the icon for this state.
     */
    color?: string;
    /**
     * The `aria-label` to apply for this state.
     */
    ariaLabel?: string;
    /**
     * The title attribute to apply to the icon for this state.
     */
    title?: string;
} & Pick<FontAwesomeIconProps, 'size'>;

export type BooleanStatusIconProps = {
    /**
     * Current status of icon
     */
    status: boolean;
    /**
     * Configuration for the `true` state
     */
    trueState?: BooleanStateIconProps;
    /**
     * Configuration for the `false` state
     */
    falseState?: BooleanStateIconProps;
    /**
     * Classes to apply to the icon element.
     */
    className?: string;
    /**
     * The icon size to use for both states. Overridden by `trueState.size` and `falseState.size`.
     */
    size?: BooleanStateIconProps['size'];
    /**
     * The icon to use for both states. Overridden by `trueState.icon` and `falseState.icon`.
     */
    icon?: BooleanStateIconProps['icon'];
} & Pick<FontAwesomeIconProps, 'aria-hidden' | 'aria-label'>;

function BooleanStatusIcon({
    status,
    trueState,
    falseState,
    size,
    icon,
    className,
    'aria-hidden': ariaHidden,
    'aria-label': ariaLabel,
}: BooleanStatusIconProps) {
    const commonProps = {
        className,
        'aria-hidden': ariaHidden,
    };

    if (status) {
        // True status icon
        return (
            <FontAwesomeIcon
                {...commonProps}
                icon={trueState?.icon ?? icon ?? faCheckCircle}
                color={trueState?.color ?? 'var(--icon-positive)'}
                title={trueState?.title ?? trueState?.ariaLabel ?? 'Online'}
                aria-label={
                    trueState?.ariaLabel ?? ariaLabel ?? trueState?.title ?? 'Online'
                }
                size={trueState?.size ?? size}
            />
        );
    }

    return (
        // False status icon
        <FontAwesomeIcon
            {...commonProps}
            icon={falseState?.icon ?? icon ?? faTimesCircle}
            color={falseState?.color ?? 'var(--error-default)'}
            title={falseState?.title ?? falseState?.ariaLabel ?? 'Offline'}
            aria-label={
                falseState?.ariaLabel ?? ariaLabel ?? falseState?.title ?? 'Offline'
            }
            size={falseState?.size ?? size}
        />
    );
}
const memoBooleanStatusIcon = memo(BooleanStatusIcon);
export { memoBooleanStatusIcon as BooleanStatusIcon };
